<script>
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import VoiceProvisioningSection from './VoiceProvisioningSection.vue';
import VoiceUsageSection from './VoiceUsageSection.vue';

export default {
  components: {
    SettingsFieldSection,
    SettingsToggleSection,
    NextInput,
    NextButton,
    Spinner,
    VoiceProvisioningSection,
    VoiceUsageSection,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      voiceEnabled: this.inbox.voice_enabled || false,
      inboundCallsEnabled: this.inbox.inbound_calls_enabled !== false,
      recordingMandatory: this.inbox.voice_recording_mode === 'mandatory',
      apiKeySid: this.inbox.api_key_sid || '',
      apiKeySecret: '',
      twimlAppSid: this.inbox.twiml_app_sid || '',
      isUpdating: false,
      isTogglingInbound: false,
      isTogglingRecording: false,
      // Minutes on screen, seconds on the wire: nobody thinks about a call limit in seconds.
      maxCallMinutes: Math.round(
        (this.inbox.voice_max_call_seconds || 600) / 60
      ),
      isSavingMaxCall: false,
    };
  },
  computed: {
    // A minute is the shortest limit that is not an accident, and four hours is where Twilio stops
    // accepting one at all.
    canSaveMaxCall() {
      const minutes = Number(this.maxCallMinutes);
      if (!Number.isInteger(minutes) || minutes < 1 || minutes > 240)
        return false;

      return minutes * 60 !== (this.inbox.voice_max_call_seconds || 600);
    },
    isVoiceConfigured() {
      return !!this.inbox.voice_configured;
    },
    hasApiKeySid() {
      return !!this.inbox.api_key_sid;
    },
    hasExistingCredentials() {
      return this.hasApiKeySid && !!this.inbox.has_api_key_secret;
    },
    needsTwimlAppSid() {
      return this.voiceEnabled && !this.isVoiceConfigured;
    },
    needsCredentials() {
      return this.voiceEnabled && !this.hasExistingCredentials;
    },
    needsApiKeySid() {
      return this.needsCredentials && !this.hasApiKeySid;
    },
    isSubmitDisabled() {
      if (!this.voiceEnabled) return false;
      if (this.needsTwimlAppSid && !this.twimlAppSid) return true;
      if (this.needsCredentials) {
        if (this.needsApiKeySid && !this.apiKeySid) return true;
        return !this.apiKeySecret;
      }
      return false;
    },
  },
  watch: {
    'inbox.voice_enabled'(val) {
      this.voiceEnabled = val || false;
    },
    'inbox.api_key_sid'(val) {
      this.apiKeySid = val || '';
    },
    'inbox.twiml_app_sid'(val) {
      this.twimlAppSid = val || '';
    },
    'inbox.inbound_calls_enabled'(val) {
      this.inboundCallsEnabled = val !== false;
    },
    'inbox.voice_max_call_seconds'(val) {
      this.maxCallMinutes = Math.round((val || 600) / 60);
    },
    'inbox.voice_recording_mode'(val) {
      this.recordingMandatory = val === 'mandatory';
    },
  },
  methods: {
    async handleMaxCallSave() {
      if (this.isSavingMaxCall || !this.canSaveMaxCall) return;
      this.isSavingMaxCall = true;
      try {
        await InboxesAPI.setMaxCallDuration(
          this.inbox.id,
          this.maxCallMinutes * 60
        );
        await this.$store.dispatch('inboxes/get', this.inbox.id);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error?.response?.data?.message ||
            this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE')
        );
      } finally {
        this.isSavingMaxCall = false;
      }
    },
    async handleRecordingToggle(newValue) {
      if (this.isTogglingRecording) return;
      const previous = this.recordingMandatory;
      this.recordingMandatory = newValue;
      this.isTogglingRecording = true;
      try {
        await InboxesAPI.setRecordingMode(
          this.inbox.id,
          newValue ? 'mandatory' : 'optional'
        );
        await this.$store.dispatch('inboxes/get', this.inbox.id);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        this.recordingMandatory = previous;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isTogglingRecording = false;
      }
    },
    async handleInboundToggle(newValue) {
      if (this.isTogglingInbound) return;
      const previousValue = this.inboundCallsEnabled;
      this.inboundCallsEnabled = newValue;
      this.isTogglingInbound = true;
      try {
        await InboxesAPI.setInboundCalls(this.inbox.id, newValue);
        await this.$store.dispatch('inboxes/get', this.inbox.id);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        this.inboundCallsEnabled = previousValue;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isTogglingInbound = false;
      }
    },
    async updateVoiceSettings() {
      this.isUpdating = true;
      try {
        const channelPayload = { voice_enabled: this.voiceEnabled };

        if (this.needsCredentials) {
          if (this.needsApiKeySid) {
            channelPayload.api_key_sid = this.apiKeySid;
          }
          channelPayload.api_key_secret = this.apiKeySecret;
        }
        if (this.needsTwimlAppSid) {
          channelPayload.twiml_app_sid = this.twimlAppSid;
        }

        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: channelPayload,
        });
        this.apiKeySecret = '';
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdating = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <SettingsToggleSection
      v-model="voiceEnabled"
      :header="$t('INBOX_MGMT.VOICE_CONFIGURATION.ENABLE_VOICE.LABEL')"
      :description="
        $t('INBOX_MGMT.VOICE_CONFIGURATION.ENABLE_VOICE.DESCRIPTION')
      "
    />

    <div
      v-if="voiceEnabled && (needsCredentials || needsTwimlAppSid)"
      class="flex flex-col gap-4"
    >
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.VOICE_CONFIGURATION.CREDENTIALS.DESCRIPTION') }}
      </p>
      <NextInput
        v-if="needsApiKeySid"
        v-model="apiKeySid"
        :label="$t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.LABEL')"
        :placeholder="$t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.PLACEHOLDER')"
      />
      <NextInput
        v-model="apiKeySecret"
        type="password"
        :label="$t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.LABEL')"
        :placeholder="
          $t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.PLACEHOLDER')
        "
      />
      <NextInput
        v-if="needsTwimlAppSid"
        v-model="twimlAppSid"
        :label="$t('INBOX_MGMT.ADD.VOICE.TWILIO.TWIML_APP_SID.LABEL')"
        :placeholder="
          $t('INBOX_MGMT.ADD.VOICE.TWILIO.TWIML_APP_SID.PLACEHOLDER')
        "
      />
    </div>

    <div
      v-if="inbox.voice_enabled"
      class="relative"
      :class="{ 'pointer-events-none opacity-60': isTogglingInbound }"
    >
      <SettingsToggleSection
        :model-value="inboundCallsEnabled"
        :header="$t('INBOX_MGMT.VOICE_CONFIGURATION.INBOUND.LABEL')"
        :description="$t('INBOX_MGMT.VOICE_CONFIGURATION.INBOUND.DESCRIPTION')"
        :hide-toggle="isTogglingInbound"
        @update:model-value="handleInboundToggle"
      >
        <template v-if="isTogglingInbound" #hiddenToggle>
          <Spinner class="size-4 text-n-slate-11" />
        </template>
      </SettingsToggleSection>
    </div>

    <div
      v-if="inbox.voice_enabled"
      class="relative"
      :class="{ 'pointer-events-none opacity-60': isTogglingRecording }"
    >
      <SettingsToggleSection
        :model-value="recordingMandatory"
        :header="$t('INBOX_MGMT.VOICE_CONFIGURATION.RECORDING.LABEL')"
        :description="
          $t('INBOX_MGMT.VOICE_CONFIGURATION.RECORDING.DESCRIPTION')
        "
        :hide-toggle="isTogglingRecording"
        @update:model-value="handleRecordingToggle"
      >
        <template v-if="isTogglingRecording" #hiddenToggle>
          <Spinner class="size-4 text-n-slate-11" />
        </template>
      </SettingsToggleSection>
    </div>

    <div v-if="inbox.voice_enabled">
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.VOICE_CONFIGURATION.MAX_CALL.LABEL')"
        :help-text="$t('INBOX_MGMT.VOICE_CONFIGURATION.MAX_CALL.DESCRIPTION')"
      >
        <div class="flex items-end gap-2">
          <NextInput
            v-model="maxCallMinutes"
            type="number"
            min="1"
            max="240"
            class="w-28"
            :message="$t('INBOX_MGMT.VOICE_CONFIGURATION.MAX_CALL.UNIT')"
          />
          <NextButton
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="isSavingMaxCall"
            :disabled="!canSaveMaxCall || isSavingMaxCall"
            size="sm"
            @click="handleMaxCallSave"
          />
        </div>
      </SettingsFieldSection>
    </div>

    <div v-if="inbox.voice_enabled && inbox.voice_call_webhook_url">
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_TITLE')"
        :help-text="
          $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_SUBTITLE')
        "
      >
        <woot-code :script="inbox.voice_call_webhook_url" lang="html" />
      </SettingsFieldSection>
      <SettingsFieldSection
        :label="
          $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_TITLE')
        "
        :help-text="
          $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_SUBTITLE')
        "
      >
        <woot-code :script="inbox.voice_status_webhook_url" lang="html" />
      </SettingsFieldSection>
    </div>

    <VoiceProvisioningSection
      v-if="inbox.voice_number_id"
      :voice-number-id="inbox.voice_number_id"
      :configured="isVoiceConfigured"
    />

    <VoiceUsageSection
      v-if="inbox.voice_number_id"
      :voice-number-id="inbox.voice_number_id"
      class="pt-6 border-t border-n-weak"
    />

    <div>
      <NextButton
        :disabled="isSubmitDisabled"
        :is-loading="isUpdating"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        @click="updateVoiceSettings"
      />
    </div>
  </div>
</template>
